import { fmtDateTime } from "@/lib/api";
import { roleLabel } from "@/lib/constants";
import { CheckCircle, XCircle, Clock, CurrencyInr, PaperPlaneTilt } from "@phosphor-icons/react";

/**
 * Visual timeline showing the lifecycle of a claim:
 *   Submitted → Chain Roles (Dept Manager / Finance Manager / Super Admin) → Payment
 * Each step displays actor, timestamp, comment and a state icon.
 * A rejection short-circuits the trail and surfaces the reason prominently.
 */
export default function ApprovalTimeline({ approval }) {
  if (!approval) return null;
  const actions = approval.actions || [];
  const chain = approval.chain || [];
  const currentStep = approval.current_step ?? 0;
  const overall = approval.status;

  const roleLbl = (r) => roleLabel(r);

  const rejectAction = actions.find((a) => a.action === "reject");
  const reverseAction = actions.find((a) => a.action === "reject_approved");
  const paidAction = actions.find((a) => a.action === "paid");
  const approvalsByStep = {};
  actions.filter((a) => a.action === "approve").forEach((a) => { approvalsByStep[a.step] = a; });

  const nodes = [];
  nodes.push({
    key: "submitted",
    title: "Submitted",
    subtitle: approval.applicant_name || "",
    ts: approval.created_at,
    state: "done",
    icon: PaperPlaneTilt,
  });

  chain.forEach((role, idx) => {
    const act = approvalsByStep[idx];
    let state = "pending";
    let actor = ""; let ts = ""; let comment = "";
    if (rejectAction && rejectAction.step === idx) {
      state = "rejected"; actor = rejectAction.actor_name; ts = rejectAction.timestamp; comment = rejectAction.comment;
    } else if (act) {
      state = "done"; actor = act.actor_name; ts = act.timestamp; comment = act.comment;
    } else if (overall === "rejected" && rejectAction && idx > rejectAction.step) {
      state = "skipped";
    } else if (idx < currentStep || overall === "approved" || overall === "paid") {
      state = "done";
    } else if (idx === currentStep && overall === "pending") {
      state = "current";
    }
    const stageDelayed = state === "current" && approval.is_delayed;
    nodes.push({
      key: `step-${idx}`,
      title: roleLbl(role),
      subtitle: actor, ts, comment, state,
      delayed: stageDelayed,
      overdueHours: stageDelayed ? approval.overdue_hours : 0,
      icon: state === "rejected" ? XCircle : state === "done" ? CheckCircle : Clock,
    });
  });

  const paidState = overall === "paid" ? "done"
    : (overall === "approved" ? "current"
    : (overall === "rejected" && !reverseAction ? "skipped" : (overall === "rejected" ? "done" : "pending")));
  nodes.push({
    key: "paid",
    title: "Payment",
    subtitle: paidAction?.actor_name || "",
    ts: paidAction?.timestamp || approval.paid_at || "",
    state: reverseAction ? "done" : paidState,
    icon: CurrencyInr,
  });

  // Reversal node (Super Admin reversed an approved/paid claim)
  if (reverseAction) {
    nodes.push({
      key: "reversed",
      title: "Reversed to Rejected",
      subtitle: reverseAction.actor_name,
      ts: reverseAction.timestamp,
      comment: reverseAction.comment,
      state: "rejected",
      icon: XCircle,
    });
  }

  const tone = (s) => {
    switch (s) {
      case "done": return { dot: "bg-green-500 text-white", line: "bg-green-400", label: "text-green-700 dark:text-green-400" };
      case "rejected": return { dot: "bg-red-500 text-white", line: "bg-red-300", label: "text-red-700 dark:text-red-400" };
      case "current": return { dot: "bg-amber-500 text-white ring-4 ring-amber-100 dark:ring-amber-900/40 animate-pulse", line: "bg-slate-300 dark:bg-slate-700", label: "text-amber-700 dark:text-amber-400" };
      case "skipped": return { dot: "bg-slate-300 text-slate-500", line: "bg-slate-200 dark:bg-slate-700", label: "text-slate-400 line-through" };
      default: return { dot: "bg-slate-200 dark:bg-slate-700 text-slate-500", line: "bg-slate-200 dark:bg-slate-700", label: "text-slate-500" };
    }
  };

  return (
    <div className="border rounded-md p-3 bg-slate-50 dark:bg-slate-800/30" style={{ borderColor: "hsl(var(--border))" }} data-testid="approval-timeline">
      <div className="text-xs uppercase tracking-wider opacity-70 mb-3">Approval Lifecycle</div>
      <ol className="relative space-y-4">
        {nodes.map((n, i) => {
          const t = tone(n.state);
          const Icon = n.icon;
          const isLast = i === nodes.length - 1;
          return (
            <li key={n.key} className="flex gap-3 relative">
              {!isLast && <span className={`absolute left-[13px] top-7 bottom-[-16px] w-0.5 ${t.line}`} aria-hidden="true" />}
              <span className={`relative z-10 flex items-center justify-center w-7 h-7 rounded-full ${t.dot} shrink-0`}>
                <Icon size={14} weight="bold" />
              </span>
              <div className="flex-1 min-w-0 pt-0.5">
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <div className={`text-sm font-medium ${t.label}`}>
                    {n.title}
                    {n.delayed && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold text-red-600 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/40 px-1.5 py-0.5 rounded">
                        Delayed · {n.overdueHours}h overdue
                      </span>
                    )}
                  </div>
                  {n.ts && <div className="text-[11px] opacity-60 whitespace-nowrap">{fmtDateTime(n.ts)}</div>}
                </div>
                {n.subtitle && <div className="text-xs opacity-75">{n.subtitle}</div>}
                {n.comment && (
                  <div className={`text-xs mt-1 rounded px-2 py-1 ${n.state === "rejected" ? "bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-900/40" : "opacity-70"}`}>
                    {n.state === "rejected" && <span className="font-semibold">Reason: </span>}
                    {n.comment}
                  </div>
                )}
                {n.state === "current" && !n.ts && (
                  <div className="text-xs opacity-60 italic mt-0.5">Awaiting action…</div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
